import * as React from "react";
import { ThumbsUp, ThumbsDown } from "lucide-react";
import { Button } from "../primitives/Button";
import { cn } from "../lib/cn";

/** A message rating: `1` helpful, `-1` not helpful, `0` none. */
export type FeedbackRating = -1 | 0 | 1;

export interface MessageFeedbackProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "onChange"> {
  /** Current rating (controlled). `1` = helpful, `-1` = not helpful, `0`/undefined = none. */
  value?: FeedbackRating;
  /**
   * Submit handler for a rating change. Receives the NEW rating (`0` when the
   * user toggles the current selection off). May return a Promise: while it is
   * pending the thumbs are disabled, and if it REJECTS the optimistic selection
   * rolls back to the previous value (no "thanks" shown). The rejection is
   * swallowed after rollback, so a failed submit never throws into the tree.
   */
  onRate: (rating: FeedbackRating) => void | Promise<void>;
  /** Prompt shown before the thumbs. Default `"Helpful?"`. */
  label?: string;
  /** Confirmation shown briefly after a non-zero rating. Default `"Thanks for your feedback!"`. */
  thanksLabel?: string;
  /** How long (ms) the thanks confirmation stays before reverting to the controls; `0` keeps it until the next value change. Default `3000`. */
  thanksDurationMs?: number;
}

const ratingToSelection = (rating: FeedbackRating | undefined): "up" | "down" | null =>
  rating === 1 ? "up" : rating === -1 ? "down" : null;

/**
 * MessageFeedback — thumbs-up / thumbs-down rating for an assistant message,
 * with optimistic selection + rollback on a failed submit (ported library-clean
 * from the AI-Expert chat).
 *
 * Controlled via `value`; the component renders the selection optimistically on
 * click and calls `onRate` with the new rating (`0` toggles the current choice
 * off). If `onRate` rejects, the selection reverts. After a successful non-zero
 * rating it shows a brief "thanks" confirmation that auto-hides after
 * `thanksDurationMs`.
 *
 * Composition: thin wrapper over two `<Button size="icon-xs" variant="ghost">`
 * thumbs. Brand tokens only — the selected thumb reads in `primary`, idle in
 * `muted-foreground`; only a `transition-colors` is used, so it is safe under
 * `prefers-reduced-motion` (no transform/opacity motion to suppress).
 */
export const MessageFeedback = React.forwardRef<HTMLDivElement, MessageFeedbackProps>(
  (
    {
      value,
      onRate,
      label = "Helpful?",
      thanksLabel = "Thanks for your feedback!",
      thanksDurationMs = 3000,
      className,
      ...rest
    },
    ref,
  ) => {
    const [selection, setSelection] = React.useState<"up" | "down" | null>(() =>
      ratingToSelection(value),
    );
    const [showThanks, setShowThanks] = React.useState(false);
    const [submitting, setSubmitting] = React.useState(false);
    const timerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

    // Sync the controlled value into the local selection when the prop changes.
    React.useEffect(() => {
      setSelection(ratingToSelection(value));
    }, [value]);

    // Clear the thanks timer on unmount.
    React.useEffect(
      () => () => {
        if (timerRef.current) clearTimeout(timerRef.current);
      },
      [],
    );

    const rate = async (type: "up" | "down"): Promise<void> => {
      const previous = selection;
      const next = selection === type ? null : type;
      const rating: FeedbackRating = next === "up" ? 1 : next === "down" ? -1 : 0;

      // Optimistic update first, then submit.
      setSelection(next);
      setShowThanks(next !== null);
      setSubmitting(true);
      try {
        await onRate(rating);
      } catch {
        // Rollback on failure; never throw into the render tree.
        setSelection(previous);
        setShowThanks(false);
        setSubmitting(false);
        return;
      }
      setSubmitting(false);
      if (next !== null && thanksDurationMs > 0) {
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => setShowThanks(false), thanksDurationMs);
      }
    };

    const thumb = (type: "up" | "down") => {
      const Icon = type === "up" ? ThumbsUp : ThumbsDown;
      const active = selection === type;
      return (
        <Button
          type="button"
          variant="ghost"
          size="icon-xs"
          motion={false}
          aria-pressed={active}
          aria-label={type === "up" ? "Helpful" : "Not helpful"}
          disabled={submitting}
          onClick={() => void rate(type)}
          className="rounded-full text-muted-foreground hover:text-foreground"
        >
          <Icon className={cn("size-3.5 transition-colors", active && "fill-primary text-primary")} />
        </Button>
      );
    };

    return (
      <div ref={ref} className={cn("flex items-center gap-1.5", className)} {...rest}>
        {showThanks ? (
          <p className="text-xs text-muted-foreground" role="status">
            {thanksLabel}
          </p>
        ) : (
          <>
            <span className="text-xs text-muted-foreground">{label}</span>
            {thumb("up")}
            {thumb("down")}
          </>
        )}
      </div>
    );
  },
);

MessageFeedback.displayName = "MessageFeedback";
